import { useEffect, useState, useCallback } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const P = (n) => `${BACKEND}/api/photos/moodboard/ref-${n}.jpg`;

// The IMAGES array is only used for the lightbox index -> photo mapping.
const IMAGES = [
  2, 15, 11, 5, 9, 19, 20, 16, 6, 13, 3, 17, 18, 14, 12, 10, 8, 4, 7,
  21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35,
  36, 37, 38, 39, 40,
];

// Curated collage: each entry lays out a small cluster of overlapping tiles.
// Fields (percentages relative to the cluster container):
//   n:      image number
//   w:      width % of container
//   x, y:   top-left corner % of container
//   z:      stacking order (higher = in front)
// Each cluster row is stacked vertically down the page.
const CLUSTERS = [
  {
    align: "left",
    h: "clamp(360px, 46vw, 620px)",
    tiles: [
      { n: 2,  w: 26, x: 2,  y: 14, z: 3 },
      { n: 11, w: 24, x: 26, y: 4,  z: 2 },
      { n: 19, w: 28, x: 48, y: 22, z: 1 },
      { n: 4,  w: 20, x: 76, y: 8,  z: 2 },
      { n: 15, w: 18, x: 80, y: 58, z: 3 },
    ],
  },
  {
    align: "right",
    h: "clamp(340px, 44vw, 580px)",
    tiles: [
      { n: 3,  w: 24, x: 2,  y: 6,  z: 3 },
      { n: 7,  w: 20, x: 24, y: 40, z: 2 },
      { n: 6,  w: 24, x: 42, y: 4,  z: 1 },
      { n: 23, w: 20, x: 64, y: 34, z: 2 },
      { n: 27, w: 18, x: 80, y: 6,  z: 1 },
    ],
  },
  {
    align: "left",
    h: "clamp(340px, 42vw, 560px)",
    tiles: [
      { n: 9,  w: 22, x: 0,  y: 8,  z: 2 },
      { n: 18, w: 18, x: 20, y: 40, z: 3 },
      { n: 24, w: 26, x: 36, y: 6,  z: 1 },
      { n: 22, w: 18, x: 60, y: 38, z: 2 },
      { n: 10, w: 18, x: 78, y: 4,  z: 1 },
      { n: 21, w: 16, x: 82, y: 50, z: 3 },
    ],
  },
  {
    align: "right",
    h: "clamp(320px, 40vw, 540px)",
    tiles: [
      { n: 16, w: 18, x: 0,  y: 8,  z: 2 },
      { n: 8,  w: 18, x: 18, y: 40, z: 1 },
      { n: 17, w: 18, x: 34, y: 4,  z: 3 },
      { n: 12, w: 16, x: 52, y: 36, z: 2 },
      { n: 25, w: 18, x: 66, y: 4,  z: 1 },
      { n: 20, w: 16, x: 82, y: 34, z: 2 },
    ],
  },
  {
    align: "left",
    h: "clamp(320px, 40vw, 540px)",
    tiles: [
      { n: 5,  w: 20, x: 2,  y: 36, z: 3 },
      { n: 13, w: 18, x: 22, y: 4,  z: 1 },
      { n: 14, w: 18, x: 38, y: 38, z: 2 },
      { n: 26, w: 18, x: 56, y: 4,  z: 1 },
      { n: 28, w: 20, x: 72, y: 30, z: 2 },
      { n: 29, w: 22, x: 76, y: 62, z: 3 },
    ],
  },
  {
    align: "right",
    h: "clamp(360px, 44vw, 600px)",
    tiles: [
      { n: 30, w: 22, x: 2,  y: 6,  z: 2 },
      { n: 31, w: 22, x: 22, y: 34, z: 3 },
      { n: 32, w: 22, x: 42, y: 6,  z: 1 },
      { n: 33, w: 18, x: 62, y: 36, z: 2 },
      { n: 34, w: 18, x: 80, y: 8,  z: 1 },
      { n: 35, w: 20, x: 78, y: 54, z: 3 },
    ],
  },
  {
    align: "left",
    h: "clamp(360px, 46vw, 620px)",
    tiles: [
      { n: 36, w: 24, x: 0,  y: 34, z: 2 },
      { n: 37, w: 18, x: 22, y: 4,  z: 3 },
      { n: 38, w: 24, x: 38, y: 36, z: 1 },
      { n: 39, w: 20, x: 60, y: 4,  z: 2 },
      { n: 40, w: 18, x: 80, y: 40, z: 3 },
    ],
  },
];

export default function MoodboardPage() {
  const [openIdx, setOpenIdx] = useState(null);

  useEffect(() => {
    document.title = "Moodboard — The Bakers";
  }, []);

  const close = useCallback(() => setOpenIdx(null), []);
  const next = useCallback(
    () => setOpenIdx((i) => (i === null ? null : (i + 1) % IMAGES.length)),
    []
  );
  const prev = useCallback(
    () =>
      setOpenIdx((i) =>
        i === null ? null : (i - 1 + IMAGES.length) % IMAGES.length
      ),
    []
  );

  useEffect(() => {
    if (openIdx === null) return;
    const handler = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [openIdx, close, next, prev]);

  const openByN = (n) => setOpenIdx(IMAGES.indexOf(n));

  return (
    <main
      data-testid="moodboard-page"
      className="min-h-screen bg-[#FAF7F2] px-4 py-6 md:px-8 md:py-10"
    >
      <p
        data-testid="moodboard-note"
        className="max-w-[540px] text-left text-[13px] md:text-sm text-[#2C1A1D]/80 italic leading-[1.7] mb-6 md:mb-8"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        Hi Matt and Laura! We are so excited to spend the day with you. Thank
        you for capturing the best day of our lives.
        <br />
        <br />
        Love,
        <br />
        Scott and Isabelle
      </p>

      <div
        data-testid="photo-groups"
        className="max-w-[540px] text-left mb-8 md:mb-10"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        <div className="text-[10px] uppercase tracking-[0.32em] text-[#76232F] mb-3">
          Photo groups
        </div>
        <ul className="text-[13px] md:text-sm text-[#2C1A1D]/85 leading-[1.85]">
          <li>&mdash; Full guest group photo</li>
          <li>&mdash; Bride immediate family</li>
          <li>&mdash; Groom immediate family</li>
          <li>&mdash; Bride &amp; groom immediate family</li>
          <li>&mdash; Full bridal party</li>
        </ul>
      </div>

      <div className="mx-auto max-w-[1500px]">
        {CLUSTERS.map((cluster, ci) => {
          const align =
            cluster.align === "left"
              ? "mr-auto ml-0"
              : cluster.align === "right"
              ? "ml-auto mr-0"
              : "mx-auto";
          return (
            <div
              key={ci}
              className={`relative ${align} ${ci > 0 ? "-mt-24 md:-mt-40" : ""}`}
              style={{ height: cluster.h, width: "min(96%, 1200px)" }}
              data-testid={`cluster-${ci}`}
            >
            {cluster.tiles.map((t) => (
              <button
                key={t.n}
                type="button"
                onClick={() => openByN(t.n)}
                aria-label="Open image"
                data-testid={`moodboard-tile-${t.n}`}
                className="group absolute p-0 border-0 bg-transparent outline-none [@media(hover:hover)]:hover:z-30 transition-transform duration-300 ease-out [@media(hover:hover)]:hover:scale-[1.04]"
                style={{
                  left: `${t.x}%`,
                  top: `${t.y}%`,
                  width: `${t.w}%`,
                  zIndex: t.z,
                }}
              >
                <img
                  src={P(t.n)}
                  alt=""
                  loading="lazy"
                  className="block w-full h-auto shadow-[0_2px_8px_rgba(44,26,29,0.18)] [@media(hover:hover)]:group-hover:shadow-[0_14px_30px_-8px_rgba(44,26,29,0.4)] transition-shadow"
                />
              </button>
            ))}
          </div>
          );
        })}
      </div>

      {openIdx !== null && (
        <div
          data-testid="moodboard-lightbox"
          className="fixed inset-0 z-[120] bg-black/90 flex items-center justify-center px-4 py-6"
          onClick={close}
        >
          <button
            type="button"
            data-testid="lightbox-close"
            onClick={(e) => {
              e.stopPropagation();
              close();
            }}
            aria-label="Close"
            className="absolute top-4 right-5 text-white/85 hover:text-white text-3xl leading-none outline-none"
          >
            &times;
          </button>
          <button
            type="button"
            data-testid="lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            aria-label="Previous"
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white text-3xl leading-none outline-none px-2 py-4"
          >
            &larr;
          </button>
          <img
            src={P(IMAGES[openIdx])}
            alt=""
            className="max-h-full max-w-full object-contain"
            data-testid="lightbox-image"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            data-testid="lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            aria-label="Next"
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white text-3xl leading-none outline-none px-2 py-4"
          >
            &rarr;
          </button>
          <div className="absolute bottom-4 left-0 right-0 text-center text-white/60 text-[11px] uppercase tracking-[0.32em]">
            {openIdx + 1} / {IMAGES.length}
          </div>
        </div>
      )}
    </main>
  );
}
